
import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { InputWithEmojiPicker } from "@/components/ui/InputWithEmojiPicker";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (title: string, detail: string) => void;
}
export const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
  open,
  onClose,
  onSave
}) => {
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    await onSave(title, detail);
    setTitle("");
    setDetail("");
    setSubmitting(false);
    onClose();
  };

  const handleCancel = () => {
    setTitle("");
    setDetail("");
    setSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="
          max-w-xl
          w-full
          p-8
          sm:p-8
          rounded-2xl
          shadow-lg
          relative
          bg-white
          dark:bg-gray-900
          border 
          border-gray-200
          dark:border-white/10
          flex flex-col 
          items-center
          justify-center
        "
        style={{
          // Force dialog to take natural height, help with overflow edge case on small screens
          minHeight: 'auto'
        }}
      >
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          <X />
        </button>
        <DialogTitle className="mb-6 text-2xl font-bold text-gray-900 dark:text-white w-full text-center">New Task</DialogTitle>
        <form 
          className="flex flex-col space-y-6 w-full max-w-xl"
          onSubmit={e => { e.preventDefault(); handleSave(); }}
        >
          <InputWithEmojiPicker
            id="task-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Task title"
            className="w-full text-lg py-3 px-4 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
            disabled={submitting}
            autoFocus
            maxLength={120}
            required
          />
          <Textarea
            id="task-detail"
            value={detail}
            onChange={e => setDetail(e.target.value)}
            placeholder="Details"
            rows={4}
            className="w-full min-h-[100px] text-base px-4 py-3 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900"
            disabled={submitting}
            maxLength={250}
          />
          <div className="flex justify-end gap-3">
            <Button 
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!title.trim() || submitting}
              variant="default"
              type="submit"
            >
              Create
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
